import { useCallback, useMemo, useState } from 'react';

import {
  useDeleteSubscriptionMutation,
  useGetSubscriptionsQuery,
  type ModelSubscription,
} from '@app/api/Subscriptions';
import { FRONT_WARNING, SUCCESS, WARNING } from '@app/constants/alerts';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import { useHeader } from '@app/providers';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';

import { SUBSCRIPTIONS_EMPTY_NAME } from '../data/constants';
import { getSubscriptionsColumns } from '../data/getSubscriptionsColumns';

import type { ChangeEventHandler } from 'react';

// Отдельного права на абонементы пока нет, поэтому реестр опирается на модуль
// «Клиенты», как и реестр дополнительных полей клиента.
export const useSubscriptions = () => {
  const {
    params: { ActiveBranchId },
  } = useUserPrint();
  const {
    userAccessRights: { Clients },
  } = useNewUserAccessRights();
  const { setHeader } = useHeader();
  const { showNotification } = useNotification();

  const [unsavedSearchTerm, setUnsavedSearchTerm] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSubscriptionId, setEditingSubscriptionId] = useState<
    number | null
  >(null);
  // Абонемент не сбрасывается при закрытии окна подтверждения, чтобы текст
  // не менялся во время анимации закрытия
  const [deletingSubscription, setDeletingSubscription] =
    useState<ModelSubscription | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { data: subscriptions = [], isFetching } = useGetSubscriptionsQuery(
    { ActiveBranchId },
    { skip: !ActiveBranchId }
  );

  const [deleteSubscription, { isLoading: isDeleting }] =
    useDeleteSubscriptionMutation();

  const canView = Boolean(Clients?.IsOn);
  const canEdit = Boolean(Clients?.IsEdit);
  const canDelete = Boolean(Clients?.IsDelete);

  // GetSubscriptions отдаёт все абонементы филиала без фильтрации,
  // поэтому поиск по названию выполняется на клиенте.
  const filteredSubscriptions = useMemo(() => {
    const normalizedTerm = searchTerm.trim().toLowerCase();

    if (!normalizedTerm) return subscriptions;

    return subscriptions.filter(({ Name }) =>
      Name?.toLowerCase().includes(normalizedTerm)
    );
  }, [subscriptions, searchTerm]);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value } }) => setUnsavedSearchTerm(value),
    []
  );

  const onSearch = useCallback(() => {
    setSearchTerm(unsavedSearchTerm);
  }, [unsavedSearchTerm]);

  const onReset = useCallback(() => {
    setUnsavedSearchTerm('');
    setSearchTerm('');
  }, []);

  const openCreateModal = useCallback(() => {
    setEditingSubscriptionId(null);
    setIsModalOpen(true);
  }, []);

  const onEdit = useCallback(({ Id }: ModelSubscription) => {
    setEditingSubscriptionId(Id);
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingSubscriptionId(null);
  }, []);

  const onDelete = useCallback((subscription: ModelSubscription) => {
    setDeletingSubscription(subscription);
    setIsDeleteModalOpen(true);
  }, []);

  const deleteModalContent = useMemo(() => {
    const name = deletingSubscription?.Name?.trim() || SUBSCRIPTIONS_EMPTY_NAME;

    return `Вы уверены, что хотите удалить абонемент «${name}»?`;
  }, [deletingSubscription]);

  const confirmDelete = useCallback(async () => {
    if (!deletingSubscription) return;

    if (!ActiveBranchId) {
      showNotification('Филиал не был распознан', FRONT_WARNING);
      return;
    }

    try {
      await deleteSubscription({
        ActiveBranchId,
        subscriptionId: deletingSubscription.Id,
      }).unwrap();

      showNotification('Абонемент удалён', SUCCESS);
      setIsDeleteModalOpen(false);
    } catch (error) {
      showNotification(handleCatchError(error), WARNING);
    }
  }, [
    ActiveBranchId,
    deleteSubscription,
    deletingSubscription,
    showNotification,
  ]);

  const columns = useMemo(
    () => getSubscriptionsColumns({ onEdit, onDelete, canEdit, canDelete }),
    [onEdit, onDelete, canEdit, canDelete]
  );

  return {
    subscriptions: filteredSubscriptions,
    columns,
    isLoading: isFetching,
    canView,
    unsavedSearchTerm,
    onChangeSearchTerm,
    onSearch,
    onReset,
    isModalOpen,
    editingSubscriptionId,
    openCreateModal,
    closeModal,
    setHeader,
    isDeleteModalOpen,
    deleteModalContent,
    isDeleting,
    setIsDeleteModalOpen,
    confirmDelete,
  };
};
